// ABOUTME: Main onboarding container with step management and transitions
// ABOUTME: Handles the 3-step onboarding flow: Welcome → Ownership → Install
// ABOUTME: After completing, opens AddObjectModal for first object registration

import { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { WelcomeStep } from "./WelcomeStep";
import { OwnershipStep } from "./OwnershipStep";
import { StepIndicator } from "./StepIndicator";
import { InstallPrompt } from "../InstallPrompt";
import { useInstallState } from "../InstallPrompt/InstallPrompt";
import "./Onboarding.scss";

// Onboarding steps - only 3 steps now, AddObject uses existing modal
export type OnboardingStep = "welcome" | "ownership" | "install";

// Sub-state for ownership step
export type OwnershipState = "ask" | "not-yet";

interface OnboardingProps {
  onComplete: (openAddObject?: boolean) => void;
}

// Step configuration for progress indicator
const STEPS: OnboardingStep[] = ["welcome", "ownership", "install"];

// Animation variants for step transitions
const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 50 : -50,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 50 : -50,
    opacity: 0,
  }),
};

export function Onboarding({ onComplete }: OnboardingProps) {
  const [currentStep, setCurrentStep] = useState<OnboardingStep>("welcome");
  const [ownershipState, setOwnershipState] = useState<OwnershipState>("ask");
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = backward
  const { installState } = useInstallState();

  // Track step index for progress indicator
  const currentStepIndex = STEPS.indexOf(currentStep);

  // Save current step to localStorage for resume on refresh
  useEffect(() => {
    localStorage.setItem("onboarding-step", currentStep);
  }, [currentStep]);

  // Load saved step on mount (optional - can be removed if always starting fresh)
  useEffect(() => {
    const savedStep = localStorage.getItem("onboarding-step") as OnboardingStep;
    if (savedStep && STEPS.includes(savedStep)) {
      setCurrentStep(savedStep);
    }
  }, []);

  // Navigation handlers
  const goToStep = useCallback((step: OnboardingStep) => {
    const newIndex = STEPS.indexOf(step);
    const oldIndex = STEPS.indexOf(currentStep);
    setDirection(newIndex > oldIndex ? 1 : -1);
    setCurrentStep(step);
  }, [currentStep]);

  const handleSkip = useCallback(() => {
    // Clear the saved step and complete onboarding
    localStorage.removeItem("onboarding-step");
    onComplete(false); // false = don't open modal, just go to home
  }, [onComplete]);

  // Step-specific handlers
  const handleWelcomeContinue = useCallback(() => {
    goToStep("ownership");
  }, [goToStep]);

  const handleOwnershipYes = useCallback(() => {
    // Skip install step if already installed
    if (installState === "installed") {
      // Complete onboarding and open add object modal
      localStorage.removeItem("onboarding-step");
      onComplete(true);
    } else {
      goToStep("install");
    }
  }, [goToStep, installState, onComplete]);

  const handleOwnershipNotYet = useCallback(() => {
    setOwnershipState("not-yet");
  }, []);

  const handleOwnershipContinueAnyway = useCallback(() => {
    // Reset ownership state and continue
    setOwnershipState("ask");
    // Skip install step if already installed
    if (installState === "installed") {
      localStorage.removeItem("onboarding-step");
      onComplete(true);
    } else {
      goToStep("install");
    }
  }, [goToStep, installState, onComplete]);

  const handleInstallComplete = useCallback(() => {
    // Complete onboarding and open add object modal
    localStorage.removeItem("onboarding-step");
    onComplete(true);
  }, [onComplete]);

  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case "welcome":
        return (
          <WelcomeStep
            onContinue={handleWelcomeContinue}
            onSkip={handleSkip}
          />
        );
      case "ownership":
        return (
          <OwnershipStep
            state={ownershipState}
            onYes={handleOwnershipYes}
            onNotYet={handleOwnershipNotYet}
            onContinueAnyway={handleOwnershipContinueAnyway}
            onSkip={handleSkip}
          />
        );
      case "install":
        return (
          <InstallPrompt
            variant="page"
            onComplete={handleInstallComplete}
            onSkip={handleInstallComplete} // Skip also completes onboarding
            showSkip={true}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="onboarding">
      {/* Progress indicator at top */}
      <div className="onboarding__header">
        <StepIndicator
          steps={STEPS.length}
          currentStep={currentStepIndex}
        />
      </div>

      {/* Step content with transitions */}
      <div className="onboarding__content">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStep}
            custom={direction}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "tween", duration: 0.3, ease: "easeOut" },
              opacity: { duration: 0.2 },
            }}
            className="onboarding__step"
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
